/**
 * ConfirmSlide - Slide 4
 * Summary panel with all selections and Start Training button
 */

import { useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { TrainingSettings, ModelId, ReferenceImage } from "../types";
import { MODELS } from "../types";
import type { LoraBaseModel } from "@/types/loras";
import { useLoraTrainingCostEstimate } from "@/hooks/useLoraTrainingCostEstimate";
import { formatCreditsExact } from "@/utils/formatCredits";

interface ConfirmSlideProps {
  selectedModel: ModelId | string | null;
  references: ReferenceImage[];
  settings: TrainingSettings;
  onStartTraining: () => void | Promise<void>;
  /** When provided, render the real LoRA base model in the summary panel. */
  realModeBaseModel?: LoraBaseModel | undefined;
  /** When true, the Start Training mutation is in flight. */
  realModeSubmitting?: boolean;
  /** When set, the last submit attempt failed; surface inline. */
  realModeError?: string | null;
  /**
   * Backend LoRA id (real mode). Drives the pre-submit flat training cost
   * estimate (power#916, R6 dispute mitigation — a previously-free action is
   * now charged, so the cost MUST be visible before "Start Training").
   */
  realModeLoraId?: string | undefined;
}

export function ConfirmSlide({
  selectedModel,
  references,
  settings,
  onStartTraining,
  realModeBaseModel,
  realModeSubmitting,
  realModeError,
  realModeLoraId,
}: ConfirmSlideProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const realMode = realModeBaseModel !== undefined;

  // Flat per-run training cost in credits (backend-priced, cost_kind "known").
  const {
    estimatedCredits,
    isLoading: creditsLoading,
    isAvailable: creditsAvailable,
  } = useLoraTrainingCostEstimate(realModeLoraId, realMode);
  const creditsText = creditsAvailable
    ? formatCreditsExact(estimatedCredits!)
    : creditsLoading
      ? "…"
      : "—";

  const model = realMode
    ? {
        id: realModeBaseModel.slug,
        name: realModeBaseModel.displayName,
        vendor: realModeBaseModel.providerSlug ?? "—",
        license: "—",
      }
    : MODELS.find((m) => m.id === selectedModel);

  // Estimate training time and cost
  const estimatedMinutes = realMode
    ? Math.max(5 * 60, Math.floor(settings.epochs * 1.2 + 10))
    : Math.floor(settings.epochs * 1.2 + 10);
  const estimatedCost = realMode
    ? null
    : Math.round(estimatedMinutes * 0.014 * 100) / 100;

  const handleConfirm = () => {
    setShowConfirmDialog(false);
    void onStartTraining();
  };

  const triggerWordCount = references.filter((r) => r.hasTriggerWord).length;

  return (
    <div>
      {/* Two column layout */}
      <div className="flex gap-6">
        {/* Left column - Config */}
        <div className="flex-1 space-y-4">
          {/* Header with estimates panel */}
          <div className="flex items-start gap-4 mb-2">
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Review and confirm
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Review your training configuration before starting.
              </p>
            </div>
            {/* Estimates panel */}
            <div className="rounded-[8px] border border-white/10 bg-[#1a1a1a] p-4 flex-shrink-0">
              {realMode ? (
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-foreground">
                      ~5–8 h
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      on g5.4xlarge
                    </div>
                  </div>
                  <div className="w-px h-10 bg-white/10" />
                  <div
                    className="text-center"
                    data-testid="training-cost-credits"
                  >
                    <div className="text-2xl font-semibold text-foreground">
                      {creditsText}
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      credits
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-foreground">
                      ~{estimatedMinutes}
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      minutes
                    </div>
                  </div>
                  <div className="w-px h-10 bg-white/10" />
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-foreground">
                      ${(estimatedCost ?? 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      estimated
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Model section */}
          <SummarySection title="Base Model">
            <SummaryItem label="Model" value={model?.name || "Not selected"} />
            <SummaryItem label="Vendor" value={model?.vendor || "-"} />
            <SummaryItem label="License" value={model?.license || "-"} />
          </SummarySection>

          {/* Settings section - 2 column grid */}
          <SummarySection title="Training Parameters">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              <SummaryItem label="Rank" value={settings.rank.toString()} />
              <SummaryItem label="Epochs" value={settings.epochs.toString()} />
              <SummaryItem
                label="Learning rate"
                value={settings.learningRate.toFixed(4)}
              />
              <SummaryItem
                label="Snapshots"
                value={
                  settings.snapshotEvery === "final"
                    ? "Final only"
                    : `Every ${settings.snapshotEvery}`
                }
              />
            </div>
          </SummarySection>

          {/* Augmentation */}
          <SummarySection title="Augmentation">
            <div className="flex flex-wrap gap-2">
              {settings.augmentation.horizontalFlip && (
                <AugBadge>Horizontal flip</AugBadge>
              )}
              {settings.augmentation.cropJitter && (
                <AugBadge>Crop jitter</AugBadge>
              )}
              {settings.augmentation.colourJitter && (
                <AugBadge>Colour jitter</AugBadge>
              )}
              {settings.augmentation.noise && <AugBadge>Noise</AugBadge>}
              {!settings.augmentation.horizontalFlip &&
                !settings.augmentation.cropJitter &&
                !settings.augmentation.colourJitter &&
                !settings.augmentation.noise && (
                  <span className="text-xs text-muted-foreground">
                    None enabled
                  </span>
                )}
            </div>
          </SummarySection>

          {/* Warning */}
          <div className="flex items-start gap-3 rounded-[8px] border border-amber-500/30 bg-amber-500/5 p-4">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-amber-500 font-medium">
                Training is a paid action
              </p>
              <p className="text-[10px] text-amber-500/70 mt-0.5">
                {realMode && creditsAvailable
                  ? `${creditsText} credits will be reserved when training starts and charged on completion. Cancelled or failed runs are not charged.`
                  : "You will be charged for the full GPU time once started."}
              </p>
            </div>
          </div>
        </div>

        {/* Right column - Training data (aligned with Base Model) */}
        <div className="flex-1 pt-[92px]">
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
            Training Data
          </h3>
          <div className="rounded-[8px] border border-white/10 bg-[#1a1a1a] p-4">
            {/* Stats row */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-semibold text-foreground">
                  {references.length}
                </span>
                <span className="text-xs text-muted-foreground">images</span>
              </div>
              <div className="w-px h-6 bg-white/10" />
              <div className="flex items-center gap-2">
                <span className="text-2xl font-semibold text-foreground">
                  {triggerWordCount}
                </span>
                <span className="text-xs text-muted-foreground">
                  with trigger
                </span>
              </div>
            </div>

            {/* Thumbnails grid */}
            <div className="grid grid-cols-6 gap-1.5">
              {references.slice(0, 18).map((ref, index) => (
                <div
                  key={ref.id}
                  className="aspect-square rounded-[4px] overflow-hidden bg-white/5"
                >
                  <img
                    src={ref.previewUrl}
                    alt={`Reference ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {references.length > 18 && (
                <div className="aspect-square rounded-[4px] bg-white/5 flex items-center justify-center">
                  <span className="text-xs text-muted-foreground">
                    +{references.length - 18}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Start button */}
          <Button
            variant="pink"
            className="w-full mt-4 h-9"
            onClick={() => setShowConfirmDialog(true)}
            disabled={Boolean(realModeSubmitting)}
          >
            {realModeSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting…
              </>
            ) : (
              "Start Training"
            )}
          </Button>
          {realModeError && (
            <p
              className="mt-2 text-xs text-red-400"
              role="alert"
              data-testid="confirm-slide-error"
            >
              {realModeError}
            </p>
          )}
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[425px] p-0 gap-0" hideCloseButton>
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle>Start training?</DialogTitle>
              {/* Close Button */}
              <button
                type="button"
                onClick={() => setShowConfirmDialog(false)}
                aria-label="Close modal"
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  transition: "all 0.15s ease",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  color: "#999",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.12)";
                  e.currentTarget.style.color = "#999";
                }}
              >
                <X style={{ width: "18px", height: "18px" }} />
              </button>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 py-5">
            <DialogDescription>
              {realMode
                ? creditsAvailable
                  ? `This will begin training your LoRA model on AWS Batch (~5–8 hours on g5.4xlarge). ${creditsText} credits will be reserved now and charged when training completes; cancelled or failed runs are not charged.`
                  : "This will begin training your LoRA model on AWS Batch. The process runs for ~5–8 hours on g5.4xlarge and cannot be cancelled cleanly once a Batch job has started."
                : `This will begin training your LoRA model. The process cannot be cancelled and you will be charged approximately $${(estimatedCost ?? 0).toFixed(2)} for GPU time.`}
            </DialogDescription>
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 pt-4 pb-6 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="pink"
              size="sm"
              onClick={handleConfirm}
            >
              Yes, Start Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Summary section component
interface SummarySectionProps {
  title: string;
  children: React.ReactNode;
}

function SummarySection({ title, children }: SummarySectionProps) {
  return (
    <div>
      <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
        {title}
      </h3>
      <div className="rounded-[8px] border border-white/10 bg-[#1a1a1a] p-4 space-y-2">
        {children}
      </div>
    </div>
  );
}

// Summary item component
interface SummaryItemProps {
  label: string;
  value: string;
}

function SummaryItem({ label, value }: SummaryItemProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

// Augmentation badge
function AugBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-2 py-1 rounded-[4px] bg-green-500/10 text-green-500 text-xs font-medium">
      {children}
    </span>
  );
}
